import React, { useMemo } from 'react';
import { useTheme, Paper, Stack, Typography, Box } from '@mui/material';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent,
} from 'echarts/components';
import ReactEchart from './ReactEchart';

echarts.use([
  PieChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent,
  CanvasRenderer,
]);

const analyticsData = [
  { id: 1, value: 60, name: 'Interior signs' },
  { id: 2, value: 45, name: 'Ground signs' },
  { id: 3, value: 35, name: 'Banners' },
  { id: 4, value: 30, name: 'Outdoor signage' },
];

const Analytics = () => {
  const theme = useTheme();

  // Identify the top-selling product
  const topProduct = useMemo(
    () => analyticsData.reduce((max, item) => (item.value > max.value ? item : max)),
    []
  );

  const chartOptions = useMemo(
    () => ({
      tooltip: { trigger: 'item' },
      legend: {
        bottom: '0%',
        itemGap: 35,
        icon: 'roundRect',
        itemWidth: 15,
        itemHeight: 15,
        textStyle: {
          padding: [0, 0, 0, 5],
          fontFamily: 'Nunito, sans-serif',
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['60%', '80%'],
          avoidLabelOverlap: false,
          startAngle: 90,
          label: { show: false },
          data: analyticsData.map((item) => ({
            ...item,
            itemStyle: {
              color:
                item.id === topProduct.id
                  ? theme.palette.success.main // Highlight top product
                  : theme.palette.action.disabled,
              borderRadius: [100, 100, 15, 15],
            },
            tooltip: { show: true },
          })),
          z: 1,
        },
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'center',
          style: {
            text: `${topProduct.name}`,
            fontWeight: 800,
            textAlign: 'center',
            fontFamily: 'Nunito, sans-serif',
            fontSize: theme.typography.h3.fontSize,
            fill: theme.palette.text.primary,
          },
        },
        {
          type: 'text',
          left: 'center',
          top: '57%',
          style: {
            text: `${topProduct.value}%`,
            textAlign: 'center',
            fontFamily: 'Nunito, sans-serif',
            fontSize: theme.typography.body1.fontSize,
            fill: theme.palette.neutral?.darker || '#666',
          },
        },
      ],
    }),
    [theme, topProduct]
  );

  return (
    <Paper sx={{ px: 2, py: 3, height: 450 }}>
      <Stack alignItems="center" spacing={2}>
        {/* Top Product Highlight Section */}
        <Box textAlign="center">
          <Typography variant="h6" color="text.primary">
            Top Selling Product
          </Typography>
          <Typography variant="h4" color="success.main">
            {topProduct.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {topProduct.value}% of total sales
          </Typography>
        </Box>

        {/* Pie Chart */}
        <ReactEchart
          echarts={echarts}
          option={chartOptions}
          sx={{ width: 400, height: 400 }}
        />
      </Stack>
    </Paper>
  );
};

export default Analytics;
